// Data retention (docs/plans/golive-privacy-tooling.md §4; privacy policy draft §8).
//
// What is purged is operational system data only: expired sessions and their IP addresses,
// expired sign-in codes, old retry keys, the detail of old integration messages, and staged
// import files. Clinical data is never deleted (CLAUDE.md domain rule 6): a category can name
// only a table on PURGEABLE_TABLES, `assertPurgeable` refuses everything else, and the
// database function and grants refuse it again (migration 0072).
//
// Every period is provisional: TODO(legal-verify) (plan P6). An organisation may change a
// period within its bounds; the defaults below apply until it does.

const DAY_MILLIS = 24 * 60 * 60 * 1000;

export const RETENTION_CATEGORIES = [
  'staff_sessions',
  'portal_sessions',
  'portal_sign_in_codes',
  'idempotency_keys',
  'integration_message_details',
  'import_files_with_patient_data',
  'import_files_other',
] as const;
export type RetentionCategory = (typeof RETENTION_CATEGORIES)[number];

export interface RetentionDefault {
  /** The tables a purge of this category changes. */
  readonly tables: readonly string[];
  readonly defaultDays: number;
  readonly minDays: number;
  readonly maxDays: number;
  /** Every default is waiting for legal review. */
  readonly provisional: true;
  /** What is purged, in words, for the settings screen. */
  readonly description: string;
}

/** TODO(legal-verify): every period (plan P6). */
export const RETENTION_DEFAULTS: Readonly<Record<RetentionCategory, RetentionDefault>> = {
  staff_sessions: {
    tables: ['sessions'],
    defaultDays: 90,
    minDays: 30,
    maxDays: 730,
    provisional: true,
    description: 'Staff sign-in sessions and their IP addresses, after they expired or ended',
  },
  portal_sessions: {
    tables: ['portal_sessions'],
    defaultDays: 90,
    minDays: 30,
    maxDays: 730,
    provisional: true,
    description: 'Patient portal sessions and their IP addresses, after they expired or ended',
  },
  portal_sign_in_codes: {
    tables: ['portal_sign_in_challenges'],
    defaultDays: 30,
    minDays: 1,
    maxDays: 365,
    provisional: true,
    description: 'Patient portal sign-in codes and their IP addresses, after they expired',
  },
  idempotency_keys: {
    tables: ['idempotency_keys'],
    defaultDays: 30,
    minDays: 7,
    maxDays: 365,
    provisional: true,
    description: 'Keys that stop a retried request being saved twice',
  },
  integration_message_details: {
    tables: ['integration_messages'],
    defaultDays: 365,
    minDays: 90,
    maxDays: 3650,
    provisional: true,
    description:
      'The details of messages to and from SHA, M-Pesa and other systems. The date, system, outcome and error code are kept',
  },
  import_files_with_patient_data: {
    tables: ['import_file_chunks', 'import_rows'],
    defaultDays: 7,
    minDays: 1,
    maxDays: 90,
    provisional: true,
    description: 'Uploaded files and staged rows of finished imports that hold patient data',
  },
  import_files_other: {
    tables: ['import_file_chunks', 'import_rows'],
    defaultDays: 30,
    minDays: 1,
    maxDays: 365,
    provisional: true,
    description: 'Uploaded files and staged rows of other finished imports',
  },
};

/** Kept by rule, shown on the schedule, never changed by the retention job. */
export const RETENTION_FIXED = [
  {
    record: 'Clinical records',
    kept: 'Never deleted. Corrected by versions and "entered in error"',
  },
  { record: 'Audit log', kept: 'Kept indefinitely. Append-only' },
  { record: 'Records requests and releases', kept: 'Kept indefinitely' },
  { record: 'Error reports', kept: '90 days, by their own daily purge' },
  { record: 'Offline drafts on a device', kept: '12 hours; cleared at sign-out' },
  { record: 'Backups', kept: 'By the backup policy' },
] as const;

/** The only tables the retention job may change. */
export const PURGEABLE_TABLES: readonly string[] = [
  ...new Set(RETENTION_CATEGORIES.flatMap((c) => RETENTION_DEFAULTS[c].tables)),
];

/**
 * Tables holding clinical or patient record data. Never purged: listed so the refusal can be
 * tested by name. Not exhaustive; anything not on PURGEABLE_TABLES is refused anyway.
 */
export const CLINICAL_TABLES = [
  'patients',
  'patient_versions',
  'patient_identifiers',
  'patient_contacts',
  'visits',
  'queue_entries',
  'consultations',
  'clinical_notes',
  'clinical_note_versions',
  'visit_diagnoses',
  'patient_problems',
  'patient_problem_versions',
  'allergy_intolerances',
  'allergy_intolerance_versions',
  'allergy_assertions',
  'observations',
  'lab_orders',
  'lab_samples',
  'lab_reports',
  'lab_results',
  'clinical_notifications',
  'prescriptions',
  'prescription_lines',
  'dispenses',
  'dispense_lines',
  'service_orders',
  'nursing_notes',
  'medication_administrations',
  'pregnancies',
  'anc_contacts',
  'labour_records',
  'maternity_deliveries',
  'immunisation_doses',
  'imaging_exams',
  'imaging_reports',
  'theatre_cases',
  'theatre_operation_notes',
  'admissions',
  'mortuary_cases',
  'referrals',
  'sick_sheets',
  'referral_letters',
  'patient_documents',
  'document_blobs',
  'records_requests',
  'record_releases',
  'audit_events',
  'visit_sensitivity_changes',
  'break_glass_reviews',
] as const;

const CLINICAL = new Set<string>(CLINICAL_TABLES);
const PURGEABLE = new Set<string>(PURGEABLE_TABLES);

/** Throws unless the table is operational data the retention job may purge. */
export function assertPurgeable(table: string): void {
  if (CLINICAL.has(table)) {
    throw new Error(`${table} holds clinical or record data, which is never purged`);
  }
  if (!PURGEABLE.has(table)) {
    throw new Error(`${table} is not an operational table the retention job may purge`);
  }
}

export type RetentionErrorCode = 'retention.days_out_of_range';

export function retentionDaysError(
  category: RetentionCategory,
  days: number,
): RetentionErrorCode | null {
  const rule = RETENTION_DEFAULTS[category];
  return Number.isInteger(days) && days >= rule.minDays && days <= rule.maxDays
    ? null
    : 'retention.days_out_of_range';
}

export interface RetentionRuleRow {
  readonly category: RetentionCategory;
  readonly keepDays: number;
}

export interface RetentionScheduleRow extends RetentionDefault {
  readonly category: RetentionCategory;
  readonly keepDays: number;
  /** True when the organisation hasn't changed the default. */
  readonly isDefault: boolean;
}

/** Every category, with the organisation's days where set. */
export function retentionSchedule(rows: readonly RetentionRuleRow[]): RetentionScheduleRow[] {
  const set = new Map(rows.map((r) => [r.category, r.keepDays]));
  return RETENTION_CATEGORIES.map((category) => {
    const rule = RETENTION_DEFAULTS[category];
    const keepDays = set.get(category);
    return {
      ...rule,
      category,
      keepDays: keepDays ?? rule.defaultDays,
      isDefault: keepDays === undefined,
    };
  });
}

/** Records older than this instant are purged. */
export function retentionCutoff(now: Date, keepDays: number): Date {
  return new Date(now.getTime() - keepDays * DAY_MILLIS);
}
